import React, { useState, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { OrderSummary, OrderFilters as OrderFiltersType } from '../../types';
import { orderService } from '../../services/orderService';
import { OrderFilters, OrderSearch, OrdersList, OrdersTable, ViewToggle, OrderDetailModal, CreateOrderModal } from '../../components/admin';
import { ordersToIcs, downloadIcs } from '../../utils/calendarExportUtils';
import app from '../../config/firebase';
import './OrdersPage.css';

const CALENDAR_FUNCTION_REGION = 'us-central1';
const projectId = (app.options as { projectId?: string }).projectId || 'the-frosting-room';
const subscriptionUrlTemplate = `https://${CALENDAR_FUNCTION_REGION}-${projectId}.cloudfunctions.net/ordersCalendar?token=YOUR_TOKEN`;

interface OrdersPageProps {
  className?: string;
}

type ViewMode = 'list' | 'table';

const OrdersPage: React.FC<OrdersPageProps> = ({ className = '' }) => {
  const [orders, setOrders] = useState<OrderSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<OrderFiltersType>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<OrderSummary | null>(null);
  const [showOrderDetail, setShowOrderDetail] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editOrderData, setEditOrderData] = useState<any>(null);
  const [showCreateOrder, setShowCreateOrder] = useState(false);
  const [showSubscriptionHelp, setShowSubscriptionHelp] = useState(false);
  const [subscriptionUrlCopied, setSubscriptionUrlCopied] = useState(false);
  const [subscriptionUrl, setSubscriptionUrl] = useState<string | null>(null);
  const [subscriptionUrlLoading, setSubscriptionUrlLoading] = useState(false);
  const [subscriptionUrlError, setSubscriptionUrlError] = useState<string | null>(null);

  // Fetch orders
  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError(null);

      const orderFilters: OrderFiltersType = {
        ...filters,
        search: searchTerm || undefined
      };

      const fetchedOrders = await orderService.getOrders(orderFilters, 100, 0);

      // Sort by delivery/collection date (most recent first).
      // If deliveryDate is missing, fall back to createdAt for stable ordering.
      const sortedOrders = [...fetchedOrders].sort((a, b) => {
        const aTime = a.deliveryDate?.getTime?.() ?? a.createdAt.getTime();
        const bTime = b.deliveryDate?.getTime?.() ?? b.createdAt.getTime();
        return bTime - aTime;
      });

      setOrders(sortedOrders);
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError('Failed to load orders. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch orders when filters or search term changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchOrders();
    }, 300); // Debounce search

    return () => clearTimeout(timeoutId);
  }, [filters, searchTerm]);

  // Initial load
  useEffect(() => {
    fetchOrders();
  }, []);

  const handleOrderClick = (order: OrderSummary) => {
    setSelectedOrder(order);
    setShowOrderDetail(true);
  };

  const handleOrderEdit = async (order: OrderSummary) => {
    try {
      // Fetch the full order details
      const fullOrder = await orderService.getOrder(order.id);
      if (!fullOrder) {
        alert('Failed to load order details');
        return;
      }

      // Transform the order data to match CreateOrderModal format
      const editData = {
        customer: fullOrder.customer,
        items: fullOrder.items.map(item => ({
          productId: item.productId || '',
          name: item.name,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          size: item.size,
          shape: item.shape,
          layers: item.layers,
          flavors: item.flavors || [],
          filling: item.filling || '',
          icing: item.icing || '',
          baseColor: item.baseColor || '',
          pipingColor: item.pipingColor || '',
          specialInstructions: item.specialInstructions || '',
          addOns: item.addOns || [],
          matchingCupcakes: item.matchingCupcakes || [],
          description: ''
        })),
        deliveryOption: fullOrder.deliveryOption,
        deliveryDate: fullOrder.deliveryDate,
        deliveryInstructions: fullOrder.deliveryInstructions,
        paymentMethod: fullOrder.payment.method,
        paymentId: fullOrder.payment.id,
        shippingAmount: fullOrder.shipping,
        adminDiscountType: fullOrder.coupon ? fullOrder.coupon.discountType : (fullOrder.couponDiscount > 0 ? 'fixed' : ''),
        adminDiscountValue: fullOrder.coupon ? fullOrder.coupon.discountValue : (fullOrder.couponDiscount > 0 ? fullOrder.couponDiscount : undefined),
        adminNotes: fullOrder.notes || ''
      };

      setEditOrderData({
        orderId: fullOrder.id,
        data: editData
      });
      setShowEditModal(true);
    } catch (err) {
      console.error('Error loading order for edit:', err);
      alert('Failed to load order for editing');
    }
  };

  const handleCloseOrderDetail = () => {
    setShowOrderDetail(false);
    setSelectedOrder(null);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditOrderData(null);
  };

  const handleFiltersChange = (newFilters: OrderFiltersType) => {
    setFilters(newFilters);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleRefresh = () => {
    fetchOrders();
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const handleExportToCalendar = () => {
    const ics = ordersToIcs(orders, 'The Frosting Roon – Orders');
    downloadIcs(ics, 'frosting-roon-orders.ics');
  };

  const ordersWithDeliveryDate = orders.filter((o) => o.deliveryDate != null);

  const displayUrl = subscriptionUrl ?? subscriptionUrlTemplate;
  const isTemplateUrl = !subscriptionUrl;

  const handleCopySubscriptionUrl = () => {
    navigator.clipboard.writeText(displayUrl);
    setSubscriptionUrlCopied(true);
    setTimeout(() => setSubscriptionUrlCopied(false), 2000);
  };

  const fetchSubscriptionUrl = async () => {
    setSubscriptionUrlLoading(true);
    setSubscriptionUrlError(null);
    try {
      const functions = getFunctions(app);
      const getCalendarSubscriptionUrl = httpsCallable<unknown, { url: string }>(functions, 'getCalendarSubscriptionUrl');
      const result = await getCalendarSubscriptionUrl({});
      setSubscriptionUrl(result.data.url);
    } catch (err: unknown) {
      const message = err && typeof err === 'object' && 'message' in err ? String((err as { message: string }).message) : 'Could not load subscription URL';
      setSubscriptionUrlError(message);
      setSubscriptionUrl(null);
    } finally {
      setSubscriptionUrlLoading(false);
    }
  };

  useEffect(() => {
    if (showSubscriptionHelp && subscriptionUrl === null && !subscriptionUrlLoading) {
      fetchSubscriptionUrl();
    }
  }, [showSubscriptionHelp, subscriptionUrl]);

  return (
    <div className={`orders-page ${className}`}>
      {/* Header */}
      <div className="orders-header">
        <div className="header-left">
          <h1>Orders</h1>
          <span className="orders-count">
            {loading ? '...' : orders.length} orders
          </span>
        </div>
        
        <div className="header-actions">
          <button
            className="create-order-btn"
            onClick={() => setShowCreateOrder(true)}
          >
            Create order
          </button>
          <button
            className="filters-toggle-btn"
            onClick={toggleFilters}
            aria-pressed={showFilters}
          >
            Filters
          </button>
          
          <ViewToggle
            activeView={viewMode === 'list' ? 'grid' : 'table'}
            onChange={(view) => setViewMode(view === 'grid' ? 'list' : 'table')}
          />
          
          <button
            className="refresh-btn"
            onClick={handleRefresh}
            disabled={loading}
          >
            Refresh
          </button>
          <button
            className="export-calendar-btn"
            onClick={handleExportToCalendar}
            disabled={loading || ordersWithDeliveryDate.length === 0}
            title={
              ordersWithDeliveryDate.length === 0
                ? 'No orders with collection/delivery dates to export'
                : 'Download .ics file to add to Apple Calendar or Google Calendar'
            }
          >
            Export to calendar
          </button>
        </div>
      </div>

      {/* Calendar subscription */}
      <div className="orders-calendar-subscription">
        <button
          type="button"
          className="calendar-subscription-toggle"
          onClick={() => setShowSubscriptionHelp(!showSubscriptionHelp)}
          aria-expanded={showSubscriptionHelp}
        >
          Subscribe to calendar (Apple / Google)
        </button>
        {showSubscriptionHelp && (
          <div className="calendar-subscription-content">
            <p className="calendar-subscription-note">
              Add the URL below in Apple Calendar (File → New Calendar Subscription) or Google Calendar (Settings → Add calendar → Subscribe to calendar). The calendar refreshes automatically (e.g. every 12 hours). See <strong>CALENDAR_SUBSCRIPTION.md</strong> in the project for full setup.
            </p>
            {subscriptionUrlLoading && <p className="calendar-subscription-status">Loading subscription URL…</p>}
            {subscriptionUrlError && (
              <p className="calendar-subscription-error">
                {subscriptionUrlError} Deployed functions do not use <code>functions/.env</code>. Set the token with:{' '}
                <code className="calendar-subscription-cmd">firebase functions:config:set calendar.token=&quot;your_token_here&quot;</code>{' '}
                then run <code>firebase deploy --only functions:getCalendarSubscriptionUrl</code>.
              </p>
            )}
            <div className="calendar-subscription-url-row">
              <code className="calendar-subscription-url">{displayUrl}</code>
              <button
                type="button"
                className="calendar-subscription-copy"
                onClick={handleCopySubscriptionUrl}
                disabled={subscriptionUrlLoading}
              >
                {subscriptionUrlCopied ? 'Copied' : isTemplateUrl ? 'Copy URL template' : 'Copy subscription URL'}
              </button>
            </div>
            {!subscriptionUrlError && subscriptionUrl && (
              <p className="calendar-subscription-region">Subscription URL includes your token. Keep it private.</p>
            )}
          </div>
        )}
      </div>

      {/* Search */}
      <div className="orders-search">
        <OrderSearch
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Search orders, customers, order numbers..."
        />
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="orders-filters">
          <OrderFilters
            filters={filters}
            onFiltersChange={handleFiltersChange}
          />
        </div>
      )}

      {/* Orders Content */}
      <div className="orders-content">
        {viewMode === 'list' ? (
          <OrdersList
            orders={orders}
            onOrderClick={handleOrderClick}
            onEdit={handleOrderEdit}
            loading={loading}
            error={error}
          />
        ) : (
          <OrdersTable
            orders={orders}
            onOrderClick={handleOrderClick}
            onEdit={handleOrderEdit}
            loading={loading}
            error={error}
          />
        )}
      </div>

      {/* Order Detail Modal */}
      <OrderDetailModal
        isOpen={showOrderDetail}
        onClose={handleCloseOrderDetail}
        orderSummary={selectedOrder}
        onOrderDeleted={fetchOrders}
      />

      {/* Edit Order Modal */}
      <CreateOrderModal
        isOpen={showEditModal}
        onClose={handleCloseEditModal}
        onSuccess={fetchOrders}
        editMode={true}
        existingOrderId={editOrderData?.orderId}
        existingOrderData={editOrderData?.data}
      />

      {/* Create order manually (phone, in-person, etc.) */}
      <CreateOrderModal
        isOpen={showCreateOrder}
        onClose={() => setShowCreateOrder(false)}
        onSuccess={fetchOrders}
      />
    </div>
  );
};

export default OrdersPage;
